<template>
    <div class="register-container">
      <div v-if="isLoading" class="loading-overlay">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading...</div>
      </div>
      <h2>Update User Information</h2>
      <form @submit.prevent="registerOrUpdateUser">
        <div class="form-group">
          <label for="employeeSelect">Select Employee:</label>
          <select v-model="selectedEmployee" @change="fetchEmployeeData">
            <option disabled value="">Please select one</option>
            <option v-for="employee in employees" :key="employee.Id" :value="employee.Id">{{ employee.Username }}</option>
          </select>
        </div>
        <div class="form-group">
          <label for="id">ID:</label>
          <input type="number" v-model.number="form.id" @input="validateId" :disabled="!selectedFields.includes('id')" required />
          <input type="checkbox" v-model="selectedFields" value="id" />
        </div>
        <div class="form-group">
          <label for="username">Username:</label>
          <input type="text" v-model="form.username" :disabled="!selectedFields.includes('username')" required />
          <input type="checkbox" v-model="selectedFields" value="username" />
        </div>
        <div class="form-group">
          <label for="password">Password:</label>
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="form.password"
            :disabled="!selectedFields.includes('password')"
          />
          <input type="checkbox" v-model="selectedFields" value="password" />
        </div>

        <div class="form-group">
          <label for="confirmPassword">Confirm Password:</label>
          <input
            :type="showPassword ? 'text' : 'password'"
            v-model="form.confirmPassword"
            :disabled="!selectedFields.includes('password')"
          />
          <span class="field-checkbox-placeholder"></span>
        </div>

        <div class="form-group show-password">
          <label></label>
          <div class="inline-control">
            <input type="checkbox" v-model="showPassword" id="showPassword" />
            <label for="showPassword">Show Password</label>
          </div>
          <span class="field-checkbox-placeholder"></span>
        </div>
        <div class="form-group">
          <label for="email">Email:</label>
          <input type="email" v-model="form.email" :disabled="!selectedFields.includes('email')" required />
          <input type="checkbox" v-model="selectedFields" value="email" />
        </div>
        <div class="form-group">
          <label for="joinDate">Join Date:</label>
          <input type="date" v-model="form.joinDate" :disabled="!selectedFields.includes('joinDate')" required />
          <input type="checkbox" v-model="selectedFields" value="joinDate" />
        </div>
        <div class="form-group">
          <label for="department">Department:</label>
          <input type="text" v-model="form.department" :disabled="!selectedFields.includes('department')" required />
          <input type="checkbox" v-model="selectedFields" value="department" />
        </div>
        <div class="form-group">
          <label for="intime">In Time:</label>
          <input
            type="time"
            v-model="form.intime"
            :disabled="!isTimeFieldSelected"
            required
          />
          <input
            type="checkbox"
            :checked="isTimeFieldSelected"
            @change="toggleTimeFields"
          />
        </div>

        <div class="form-group">
          <label for="outtime">Out Time:</label>
          <input
            type="time"
            v-model="form.outtime"
            :disabled="!isTimeFieldSelected"
            required
          />
          <span class="field-checkbox-placeholder"></span>
        </div>
        <div class="form-group">
          <label for="systemAccessLevel">System Access Level:</label>
          <select v-model="form.systemAccessLevel" :disabled="!selectedFields.includes('systemAccessLevel')" required>
            <option value="Staff">Staff</option>
            <option value="Management">Management</option>
            <option value="Executive">Executive</option>
            <option value="Administrator">Administrator</option>
          </select>
          <input type="checkbox" v-model="selectedFields" value="systemAccessLevel" />
        </div>

        <div class="form-group">
          <label for="hasSupervisor">Has Supervisor:</label>
          <select
            id="hasSupervisor"
            v-model="form.hasSupervisor"
            @change="handleSupervisorToggle"
          >
            <option :value="true">Yes</option>
            <option :value="false">No</option>
          </select>
          <input
            type="checkbox"
            :checked="selectedFields.includes('supervisorName')"
            @change="toggleSelectedField('supervisorName')"
          />
        </div>

        <div class="form-group">
          <label for="supervisorName">Supervisor Name:</label>
          <select
            id="supervisorName"
            v-model="form.supervisorName"
            :required="form.hasSupervisor"
            :disabled="!form.hasSupervisor || !selectedFields.includes('supervisorName')"
          >
            <option value="">Select Supervisor</option>
            <option
              v-for="employee in supervisorCandidates"
              :key="employee.Id"
              :value="employee.Username"
            >
              {{ employee.Username }}
            </option>
          </select>
          <span class="field-checkbox-placeholder"></span>
        </div>

        <div class="form-group">
        <label for="resigned">Resigned:</label>
        <select
          id="resigned"
          v-model="form.resigned"
          :disabled="!selectedFields.includes('resigned')"
        >
          <option :value="false">No</option>
          <option :value="true">Yes</option>
        </select>
        <input type="checkbox" v-model="selectedFields" value="resigned" />
      </div>
        <button type="submit">{{ selectedEmployee ? 'Update' : 'Update' }}</button>
      </form>
    </div>
  </template>

<script>
import axios from 'axios'

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL

export default {
  data () {
    return {
      employees: [],
      selectedEmployee: '',
      selectedFields: [],
      form: {
        id: null,
        username: '',
        password: '',
        confirmPassword: '',
        email: '',
        joinDate: '',
        department: '',
        systemAccessLevel: 'Staff',
        intime: '',
        outtime: '',
        hasSupervisor: false,
        supervisorName: '',
        resigned: false,
        loadingCount: 0
      },
      showPassword: false
    }
  },
  computed: {
    isLoading () {
      return this.loadingCount > 0
    },
    supervisorCandidates () {
      return this.employees
        .filter(employee => employee.SystemAccessLevel !== 'Staff')
        .filter(employee => Number(employee.Resigned) !== 1)
        .sort((a, b) => a.Username.localeCompare(b.Username))
    }
  },
  methods: {
    startLoading () {
      this.loadingCount++
    },

    finishLoading () {
      this.loadingCount = Math.max(this.loadingCount - 1, 0)
    },

    withLoading (request) {
      this.startLoading()

      return request()
        .finally(() => {
          this.finishLoading()
        })
    },
    async fetchEmployees () {
      try {
        const token = localStorage.getItem('authToken')
        const response = await this.withLoading(() => axios.get(`${API_BASE_URL}/employees`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }))
        this.employees = response.data
      } catch (error) {
        this.showMessage('Error fetching employees: ' + error.message)
      }
    },
    async fetchEmployeeData () {
      if (!this.selectedEmployee) return

      try {
        const token = localStorage.getItem('authToken')
        const response = await this.withLoading(() => axios.get(`${API_BASE_URL}/update-employee-data/${this.selectedEmployee}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }))
        const employee = response.data
        this.selectedFields = []

        this.form.id = employee.Id
        this.form.username = employee.Username
        this.form.email = employee.Email
        this.form.joinDate = employee.JoinDate ? new Date(employee.JoinDate).toISOString().substr(0, 10) : ''
        this.form.department = employee.Department
        this.form.systemAccessLevel = employee.SystemAccessLevel
        this.form.intime = employee.InTime || employee.Intime || ''
        this.form.outtime = employee.OutTime || employee.Outtime || ''
        this.form.supervisorName = employee.SupervisorName || ''
        this.form.hasSupervisor = !!employee.SupervisorName
        this.form.resigned = employee.Resigned === 1 || employee.Resigned === true
      } catch (error) {
        this.showMessage('Error fetching employee data: ' + error.message)
      }
    },
    async registerOrUpdateUser () {
      if (this.form.password && this.form.password !== this.form.confirmPassword) {
        this.showMessage('Passwords do not match!')
        return
      }

      if (this.isTimeFieldSelected && !this.validateTimeDifference()) {
        this.showMessage('The difference between the Out Time and the In Time must be 8:30: regular work hours for 7:30 and 1 hour break')
        this.form.intime = ''
        this.form.outtime = ''
        return
      }

      try {
        const token = localStorage.getItem('authToken')
        if (!this.form.hasSupervisor) {
          this.form.supervisorName = null
        }

        const updatedFields = {}
        this.selectedFields.forEach(field => {
          updatedFields[field] = this.form[field]
        })

        if (this.selectedFields.includes('resigned')) {
          updatedFields.resigned = this.form.resigned
        }

        if (this.selectedFields.includes('supervisorName')) {
          updatedFields.hasSupervisor = this.form.hasSupervisor
          updatedFields.supervisorName = this.form.hasSupervisor ? this.form.supervisorName : null
        }

        if (this.selectedFields.includes('supervisorName')) {
          updatedFields.hasSupervisor = this.form.hasSupervisor
          updatedFields.supervisorName = this.form.hasSupervisor ? this.form.supervisorName : null
        }

        if (this.selectedEmployee) {
          // 更新
          await axios.put(`${API_BASE_URL}/update-employee/${this.selectedEmployee}`, updatedFields, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          })
          this.showMessage('User updated successfully!')
        } else {
          // 登録
          await this.withLoading(() => axios.post(`${API_BASE_URL}/register`, this.form, {
            headers: {
              Authorization: `Bearer ${token}`
            }
          }))
          this.showMessage('User registered successfully!')
        }
      } catch (error) {
        if (error.response && error.response.data) {
          this.showMessage('Error: ' + error.response.data)
        } else {
          this.showMessage('Error: ' + error.message)
        }
      }
    },
    handleSupervisorToggle () {
      if (!this.form.hasSupervisor) {
        this.form.supervisorName = ''
      }

      if (!this.selectedFields.includes('supervisorName')) {
        this.selectedFields.push('supervisorName')
      }
    },
    toggleSelectedField (field) {
      if (this.selectedFields.includes(field)) {
        this.selectedFields = this.selectedFields.filter(selectedField => selectedField !== field)
      } else {
        this.selectedFields.push(field)
      }
    },

    toggleTimeFields () {
      if (this.isTimeFieldSelected) {
        this.selectedFields = this.selectedFields.filter(field => field !== 'intime' && field !== 'outtime')
      } else {
        if (!this.selectedFields.includes('intime')) {
          this.selectedFields.push('intime')
        }
        if (!this.selectedFields.includes('outtime')) {
          this.selectedFields.push('outtime')
        }
      }
    },

    validateId (event) {
      const value = event.target.value
      if (value < 1) {
        this.form.id = ''
      }
    },
    validateTimeDifference () {
      const intime = this.form.intime
      const outtime = this.form.outtime

      if (!intime || !outtime) {
        return false
      }

      const [inHours, inMinutes] = intime.split(':').map(Number)
      const [outHours, outMinutes] = outtime.split(':').map(Number)

      const inTimeInMinutes = inHours * 60 + inMinutes
      const outTimeInMinutes = outHours * 60 + outMinutes

      const differenceInMinutes = outTimeInMinutes - inTimeInMinutes

      return differenceInMinutes === 510 // 8 hours 30 minutes = 510 minutes
    },
    showMessage (message) {
      alert(message)
    }
  },
  mounted () {
    this.fetchEmployees()
  }
}
</script>

  <style scoped>
  .register-container {
    max-width: 620px;
    margin: auto;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  h2 {
    text-align: center;
  }

  form {
    width: 100%;
  }

  .form-group {
    display: flex;
    align-items: center;
    margin-bottom: 15px;
  }

  .form-group label {
  width: 190px;
  margin-right: 10px;
  text-align: right;
  flex-shrink: 0;
  }

  .form-group input[type="text"],
  .form-group input[type="number"],
  .form-group input[type="password"],
  .form-group input[type="email"],
  .form-group input[type="date"],
  .form-group input[type="time"],
  .form-group select {
    flex: 1;
    padding: 8px;
  }

  .form-group input[type="checkbox"] {
    margin-left: 10px;
  }

  .inline-control {
    width: 240px;
    flex: 0 0 240px;
    display: flex;
    align-items: center;
  }

  .inline-control input[type="checkbox"] {
    margin-left: 0;
    margin-right: 6px;
  }

  .inline-control label {
    width: auto;
    margin: 0;
    text-align: left;
  }

  .field-checkbox-placeholder {
    width: 24px;
    flex: 0 0 24px;
  }

  button {
    width: 100%;
    padding: 10px;
    background-color: #007bff;
    border: none;
    color: white;
    cursor: pointer;
  }

  button:hover {
    background-color: #0056b3;
  }

  input,
  select {
    flex: 1;
    padding: 5px;
    box-sizing: border-box;
  }

  .form-group input[type="checkbox"] {
  margin-left: 10px;
  width: 14px;
  flex: 0 0 14px;
  }

  .loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(255, 255, 255, 0.65);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  z-index: 9999;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 5px solid #ccc;
  border-top: 5px solid #333;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-text {
  margin-top: 12px;
  font-weight: bold;
  font-size: 16px;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
  </style>
